import { useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";

export type FeatureFlagGateStatus = "pending" | "enabled" | "disabled";

/**
 * Resolves a boolean LaunchDarkly flag into a three-state gate, waiting for the
 * client to finish initializing before deciding so callers never act on an
 * unresolved flag. Once ready, anything other than an explicit `true` (flag off,
 * absent, or malformed) reads as "disabled".
 */
export const useFeatureFlagGate = (flagValue: unknown): FeatureFlagGateStatus => {
  const ldClient = useLDClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Reset to pending whenever the client identity changes (or is torn down) so a
    // new, not-yet-initialized client can't inherit the previous one's ready state.
    setReady(false);
    // A falsy client just means the provider hasn't created it yet — wait for it
    // rather than assuming ready, to avoid deciding on an unresolved flag value.
    if (!ldClient) return;
    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setReady(true);
    };
    // Two-arg then() handles success AND rejection (failed init) without leaving
    // an unhandled rejection — either way the gate resolves to a decision.
    void ldClient.waitUntilReady().then(markReady, markReady);
    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  if (!ready) return "pending";
  return flagValue === true ? "enabled" : "disabled";
};
